<template>
    <div class="reservation-service-box-wrap reservation-service-seating" v-show="serviceVisible">
        <div class="initial-section">
            <div class="image" :style="getImage()" />

            <div class="content">
                <h3>{{ name }}</h3>

                <p v-html="description" />

                <div class="price">
                    <span class="price-number">{{ formattedPrice(price, billedPerDay) }}</span>

                    <span class="price-unit">/ cestujúci</span>
                </div>

                <div class="ordering-controls" v-show="!moreSectionVisible">
                    <div class="order-specific" />

                    <div class="order-all">
                        <button type="button" class="btn-primary-border" @click="openMoreSection">Objednať miestenky</button>
                    </div>
                </div>
            </div>
        </div>

        <div v-show="moreSectionVisible" class="more-section bus-seating">
            <div class="bus-plan">
                <Seat :type="SeatType.Occupied" />

                <div class="customer-seats">
                    <SeatingPlan53Mercedes v-if="seatingPlan === SeatingPlan.Mercedes53" />

                    <SeatingPlan51Mercedes v-else-if="seatingPlan === SeatingPlan.Mercedes51" />

                    <SeatingPlan63Scania v-else-if="seatingPlan === SeatingPlan.Scania63" />

                    <SeatingPlan48Setra v-else-if="seatingPlan === SeatingPlan.Setra48" />

                    <SeatingPlan49Setra v-else />
                </div>
            </div>

            <div class="seating-info">
                <div class="seating-legend">
                    <div class="reservation-box">
                        <div class="legend-seat">
                            <div class="square" />

                            <span>Voľné</span>
                        </div>

                        <div class="legend-seat">
                            <div class="square active" />

                            <span>Vybrané</span>
                        </div>

                        <div class="legend-seat">
                            <div class="square occupied" />

                            <span>Obsadené</span>
                        </div>
                    </div>
                </div>

                <div class="seating-travellers">
                    <div class="reservation-box">
                        <div class="header">
                            <h3>Cestujúci</h3>
                        </div>

                        <SeatingServiceTraveller
                            v-for="(traveller) in reservationState.travellers"
                            :key="`traveller-seating-service-${(traveller as Traveller).hash}`"
                            :firstName="(traveller as Traveller).first_name"
                            :lastName="(traveller as Traveller).last_name"
                            :hash="(traveller as Traveller).hash"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import Seat from './Seat.vue';
import {
    SeatingPlan, SeatStatus, SeatType, Service, Traveller,
} from '../../types';
import SeatingPlan49Setra from './SeatingPlan49Setra.vue';
import SeatingPlan51Mercedes from './SeatingPlan51Mercedes.vue';
import SeatingPlan53Mercedes from './SeatingPlan53Mercedes.vue';
import SeatingPlan63Scania from './SeatingPlan63Scania.vue';
import SeatingServiceTraveller from './SeatingServiceTraveller.vue';
import { useReservation } from '../../modules/useReservation';
import { useSeatReservation } from '../../modules/useSeatReservation';
import { usePrice } from '../../modules/usePrice';
import { useLocalTravellers } from '../../modules/useLocalTravellers';
import SeatingPlan48Setra from './SeatingPlan48Setra.vue';

interface ServiceProps {
    service: Service;
}

const props = defineProps<ServiceProps>();

// eslint-disable-next-line vue/no-setup-props-destructure
const {
    price,
    description,
    name,
} = props.service;

// eslint-disable-next-line vue/no-setup-props-destructure
const billedPerDay = props.service.is_billed_per_day;
// eslint-disable-next-line vue/no-setup-props-destructure
const image = props.service.reservation_image;

const seatingPlan = window.RESERVATION_STORE.tripBaseInfo.tripDate.seating_plan;

const { reservationState } = useReservation();
const { checkSeats, autoChooseSelectedTraveller } = useSeatReservation();
const { localTravellersRef, selectServiceForLocalTraveller } = useLocalTravellers();
const { formattedPrice } = usePrice();

const serviceVisible = ref(true);

const moreSectionVisible = ref(false);

const openMoreSection = () => {
    moreSectionVisible.value = true;
};

const getImage = () => `background-image: url(${image});`;

const mountedMethod = async () => {
    const response = await checkSeats();

    let checkVisibility = false;

    response.data.forEach((seat) => {
        if ([SeatStatus.Selected, SeatStatus.Available].includes(seat.status)) {
            checkVisibility = true;
        }

        if (seat.status === SeatStatus.Selected) {
            if (!moreSectionVisible.value) {
                openMoreSection();
            }

            const travellerIndex = localTravellersRef.value.findIndex((item) => item.hash === seat.traveller_hash);
            selectServiceForLocalTraveller(travellerIndex, props.service);
        }
    });

    autoChooseSelectedTraveller();

    if (!checkVisibility) {
        serviceVisible.value = checkVisibility;
    }
};

onMounted(() => {
    mountedMethod();
});
</script>
